import { z } from "zod";

/**
 * Member Chat API Schemas
 * Following Standard Schema V1 principles and API Schema Rubric
 */

// ============================================================================
// Base Chat Schema
// ============================================================================

export const chatZodSchema = z.object({
	chatId: z.coerce.number().int().positive(),
	memberId: z.coerce.number().int().positive(),
	contents: z.coerce.string(),
	contentType: z
		.enum(["text", "image", "video", "audio", "file", "sticker"])
		.optional(),
	source: z.enum(["user", "manager"]),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional(),
});

// ============================================================================
// Path Parameters
// ============================================================================

export const getChatPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to get chat history for"),
});

export const replyChatPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to reply to"),
});

// ============================================================================
// Query Parameters
// ============================================================================

export const getChatQueryParamsSchema = z
	.object({
		sort: z
			.enum(["asc", "desc"])
			.default("asc")
			.describe("Sort order for chat messages"),
	})
	.optional();

// ============================================================================
// Request Bodies
// ============================================================================

export const replyChatMutationRequestSchema = z.object({
	contents: z.coerce
		.string()
		.min(1)
		.max(5000)
		.describe("Reply message content"),
	contentType: z
		.enum(["text", "image", "video", "audio", "file", "sticker"])
		.default("text")
		.describe("Type of message"),
});

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * @description Chat history retrieved successfully
 */
export const getChat200Schema = z.object({
	chats: z.array(chatZodSchema),
	totalCount: z.coerce.number().int().min(0),
	unreadCount: z.coerce.number().int().min(0).optional(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

/**
 * @description Reply sent successfully
 */
export const replyChat200Schema = z.object({
	chat: chatZodSchema,
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

// ============================================================================
// Error Response Schemas
// ============================================================================

/**
 * Standard response schema for errors
 */
export const chatResponseSchema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const getChat400Schema = z.lazy(() => chatResponseSchema);

/**
 * @description Member not found
 */
export const getChat404Schema = z.lazy(() => chatResponseSchema);

/**
 * @description Internal server error
 */
export const getChat500Schema = z.lazy(() => chatResponseSchema);

/**
 * @description Bad request
 */
export const replyChat400Schema = z.lazy(() => chatResponseSchema);

/**
 * @description Member not found
 */
export const replyChat404Schema = z.lazy(() => chatResponseSchema);

/**
 * @description Internal server error
 */
export const replyChat500Schema = z.lazy(() => chatResponseSchema);

// ============================================================================
// Convenience Aliases
// ============================================================================

export const getChatQueryResponseSchema = z.lazy(() => getChat200Schema);

export const replyChatMutationResponseSchema = z.lazy(() => replyChat200Schema);
